import { AppImageDisplayInfo } from "../AppIcon/common";
import { AppColorViewModel } from "../Apps/common";
import { environmentHttpClient } from "../httpClient";
import { HubItemMediaPreviewViewModel, NoneHubMediaPreviewViewModel } from "../Hub/common";

export type AppUsageStatisticModel = {
	appId: number | null;
	appImageDisplayInfo: AppImageDisplayInfo | null;
	appColorViewModel: AppColorViewModel | null;
	appName: string;
	userCount: number;
};

export type AppUsageStatisticsSummary = {
	usedAppsCount: number;
	appUsage: readonly AppUsageStatisticModel[];
};

export function getAppUsage(
	days: number,
	count: number | null = null,
	signal?: AbortSignal
): Promise<AppUsageStatisticsSummary> {
	return environmentHttpClient.get<AppUsageStatisticsSummary>(
		"UiApi/Analytics/Statistics/AppUsage",
		{ days, count },
		{ signal }
	);
}

export function getActiveUsersCount(
	days: number,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.get<number>(
		"UiApi/Analytics/Statistics/ActiveUsersCount",
		{ days },
		{ signal }
	);
}

export enum HubItemStatisticKind {
	AnnouncementUniqueViews = 1,
	AnnouncementComments = 2,
	AnnouncementReactions = 3,
	EventAttendance = 4,
	KnowledgeItemUniqueViews = 5,
	KnowledgeItemTotalViews = 6,
	EventUniqueViews = 7,
	EventComments = 8,
	EventReactions = 9,
	KnowledgeItemComments = 10,
	KnowledgeItemReactions = 11
};

export type HubItemStatisticSummaryModel = {
	total: number;
	totalNumberOfItems: number;
	statistics: readonly HubItemStatisticModel[];
};

type BaseHubItemStatisticModel = {
	value: number;
};

export type ExistingHubItemStatisticModel = BaseHubItemStatisticModel & {
	hubItemId: number;
	hubItemTitle: string;
	hubItemMedia: HubItemMediaPreviewViewModel;
	status: Exclude<HubItemStatisticsStatus, HubItemStatisticsStatus.Deleted | HubItemStatisticsStatus.NoAccess>;
};

export type DeletedHubItemStatisticModel = BaseHubItemStatisticModel & {
	hubItemId: null;
	hubItemTitle: null;
	hubItemMedia: NoneHubMediaPreviewViewModel;
	status: HubItemStatisticsStatus.Deleted;
};

export type NoAccessHubItemStatisticModel = BaseHubItemStatisticModel & {
	hubItemId: null;
	hubItemTitle: null;
	hubItemMedia: NoneHubMediaPreviewViewModel;
	status: HubItemStatisticsStatus.NoAccess;
};

export type HubItemStatisticModel =
	| ExistingHubItemStatisticModel
	| DeletedHubItemStatisticModel
	| NoAccessHubItemStatisticModel;

export enum HubItemStatisticsStatus {
	Published = 1,
	NotPublished = 2,
	Deleted = 3,
	NoAccess = 4
}

export async function getHubStatistics(
	days: number,
	kind: HubItemStatisticKind,
	count: number,
	signal?: AbortSignal
): Promise<HubItemStatisticSummaryModel> {
	return environmentHttpClient.get<HubItemStatisticSummaryModel>(
		"UiApi/Analytics/Statistics/Hub",
		{
			days,
			kind,
			count
		},
		{ signal }
	);
}

export function ensureAnalyticsEnabled(
	signal?: AbortSignal
): Promise<null> {
	return environmentHttpClient.get<null>(
		"UiApi/Analytics/Statistics",
		undefined,
		{ signal }
	);
}